import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { API_STATUS_FAILED } from '@/constants/error-codes';
import { legacyRestFetch } from '@/features/account-settings/legacyRestFetch';
import { teamAppsQueryCreator } from '@/query/teamAppsQueryCreator';
import { userProfileQueryCreator } from '@/query/userProfileQueryCreator';
import { openDialog } from '@/shared/modals/openDialog';
import { BUILT_IN_APPS } from '@/shared/types/apps';
import type { FileFormatSettings } from '@/shared/types/export';
import { currentTimeZone } from '@/shared/utils/time/timezone';
import { ExportSettingsModal } from '../components/ExportSettingsModal';
import { getConnectedAppsQueryCreator } from '../query/getConnectedAppsQueryCreator';
import type { DropboxSyncedAccount } from '../type/apps';
import { createTranscriptExportSettings } from '../utils/export';
import { useExportFileConfiguration } from './useExportFileConfiguration';
import { useSyncedAccounts } from './useSyncedAccounts';

/**
 * Function and variables for the Dropbox export settings modal.
 *
 * @example
 * ```tsx
 * function Example() {
 *   const { canShowModal, openDropboxExportSettingsModal } =
 *     useDropboxExportSettings();
 *   const [isUpdating, setIsUpdating] = useState(false);
 *
 *   const handle = async () => {
 *     setIsUpdating(true);
 *     await openDropboxExportSettingsModal();
 *     setIsUpdating(false);
 *   }
 *
 *   return (
 *     <div>
 *       {isUpdating && ('Updating...')}
 *       <Button onClick={handle}>
 *         Update Dropbox Export Settings
 *       </Button>
 *       {isUpdating && <p>Updating...</p>}
 *     </div>
 *   );
 * ```
 */
export function useDropboxExportSettings(): {
  canEditExportSettings: boolean;
  isDropboxExportEnabled: boolean;
  openDropboxExportSettingsModal: () => Promise<boolean>;
  updateDropboxExportEnabled: (args: {
    enabled: boolean;
    dropboxAccount: DropboxSyncedAccount;
  }) => Promise<void>;
} {
  const exportFileConfigurations = useExportFileConfiguration();
  const { accounts } = useSyncedAccounts({
    service: BUILT_IN_APPS.DROPBOX,
  });
  const { updateDropboxExportSettings } = useSaveDropboxExportSettings();
  const { updateDropboxExportEnabled } = useUpdateDropboxExportEnabled();

  const openDropboxExportSettingsModal = useCallback(async () => {
    // 1. Get the user's export settings from the API
    const account = accounts[0] as DropboxSyncedAccount;
    if (!account) {
      throw new Error('No Dropbox account to configured');
    }

    const fileFormatSettings = createTranscriptExportSettings(account);

    // 2. Show the modal to update the settings
    const updatedSettings = await openDialog<null | FileFormatSettings>(
      async ({ done }) => {
        return (
          <ExportSettingsModal
            fileFormatConfigs={[
              // Copied the order from the Angular version.
              // No idea why it's ordered this way.
              exportFileConfigurations.txt,
              exportFileConfigurations.docx,
              exportFileConfigurations.pdf,
              exportFileConfigurations.srt,
            ]}
            fileFormatSettings={fileFormatSettings}
            onCancel={() => done(null)}
            onSave={(updated) => done(updated)}
          />
        );
      },
    );

    if (!updatedSettings) {
      return false;
    }
    // 3. Save the user's export settings to the API
    await updateDropboxExportSettings({
      updatedSettings,
      timezone: currentTimeZone,
      account,
    });
    return true;
  }, [accounts, exportFileConfigurations, updateDropboxExportSettings]);

  const isDropboxExportEnabled = (accounts as DropboxSyncedAccount[]).some(
    (account) => account.auto_export,
  );

  return {
    canEditExportSettings: true, // accounts.length > 1,
    openDropboxExportSettingsModal,
    updateDropboxExportEnabled,
    isDropboxExportEnabled,
  };
}

/**
 * Get the function to save the Dropbox export settings.
 */
function useSaveDropboxExportSettings() {
  const queryClient = useQueryClient();
  const { mutateAsync: updateDropboxExportSettings } = useMutation({
    mutationFn: async ({
      updatedSettings,
      account,
      timezone,
    }: {
      updatedSettings: FileFormatSettings;
      account: DropboxSyncedAccount;
      timezone: string;
    }) => {
      const body = new FormData();
      body.append('dropbox_account_id', account.dropbox_account_id);
      body.append('export_format', updatedSettings.selectedFormatKey);
      body.append('time_zone', timezone);

      for (const [key, value] of Object.entries(updatedSettings.optionValues)) {
        body.append(key, value.toString());
      }

      const response = await legacyRestFetch('dropbox_export_config', {
        method: 'POST',
        body,
      });

      if (!response.ok) {
        throw new SavingDropboxExportSettingsApiError(response);
      }

      if (response.status !== 200) {
        throw new SavingDropboxExportSettingsApiError(response);
      }

      const data = await response.json();
      if (data.status === API_STATUS_FAILED) {
        throw new SavingDropboxExportSettingsError(data);
      }

      return data;
    },
    onSuccess: () =>
      Promise.allSettled([
        queryClient.invalidateQueries(userProfileQueryCreator()),
        queryClient.invalidateQueries(teamAppsQueryCreator()),
        queryClient.invalidateQueries(getConnectedAppsQueryCreator()),
      ]),
  });

  return {
    updateDropboxExportSettings,
  };
}

function useUpdateDropboxExportEnabled() {
  const queryClient = useQueryClient();
  const { mutateAsync: updateDropboxExportEnabled } = useMutation({
    mutationFn: async ({
      enabled,
      dropboxAccount,
    }: {
      enabled: boolean;
      dropboxAccount: DropboxSyncedAccount;
    }) => {
      const body = new FormData();
      body.append('auto_import', true.toString());
      body.append('auto_export', enabled.toString());
      body.append('dropbox_account_id', dropboxAccount.dropbox_account_id);

      const response = await legacyRestFetch('dropbox_config', {
        method: 'POST',
        body,
      });

      if (!response.ok) {
        throw new SavingDropboxExportSettingsApiError(response);
      }

      if (response.status !== 200) {
        throw new SavingDropboxExportSettingsApiError(response);
      }

      const data = await response.json();
      if (data.status === API_STATUS_FAILED) {
        throw new SavingDropboxExportSettingsError(data);
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(userProfileQueryCreator());
      queryClient.invalidateQueries(teamAppsQueryCreator());
    },
  });

  return {
    updateDropboxExportEnabled,
  };
}

class SavingDropboxExportSettingsApiError extends Error {
  name = 'SavingDropboxExportSettingsApiError';
  constructor(public response: Response) {
    super('An error occurred while calling the Dropbox export settings API');
  }
}

class SavingDropboxExportSettingsError extends Error {
  name = 'SavingDropboxExportSettingsError';
  constructor(
    public data: {
      status: string;
      message: string;
    },
  ) {
    super('An error occurred while saving the Dropbox export settings');
  }
}
